// Port of ios/Sources/ViewModels/ChannelSettingsForm.swift.
//
// The pure half of channel settings (shared/API.md §27): which of a channel's properties this
// reader may change, which of them the channel even HAS, and what to send on save.
//
// WHY THE URL SLUG IS NOT EDITABLE. Changing the URL silently invalidates every permalink already
// pasted into other conversations — on the desktop that is a decision made in front of a warning,
// on a phone it would be one made in front of a soft keyboard. The slug stays what creation
// derived; the name people read is fully editable, which is the half that actually goes wrong and
// needs fixing.
import * as CreateChannelForm from './createChannelForm';
import type { ChannelRecord } from './records';
import { characterCount, characters } from './swiftCharacters';

export const minDisplayName = CreateChannelForm.minDisplayName;
export const maxDisplayName = CreateChannelForm.maxDisplayName;

/**
 * Mattermost's own column limits. Enforced here so a long paste is cut at the field rather than
 * rejected by a 400 after the round trip.
 */
export const maxPurpose = 250;
export const maxHeader = 1024;

export type Field = 'displayName' | 'purpose' | 'header';

/** The three editable strings, empty rather than null — a form has no absent fields. */
export interface Values {
  displayName: string;
  purpose: string;
  header: string;
}

/**
 * What to actually send: a field is present only when it CHANGED, so a concurrent edit to a
 * property this person never touched is not overwritten with the value they loaded.
 */
export interface Patch {
  displayName?: string;
  purpose?: string;
  header?: string;
}

export function isEmptyPatch(patch: Patch): boolean {
  return patch.displayName === undefined && patch.purpose === undefined && patch.header === undefined;
}

/**
 * - `invalid` — 400: the server rejected a value; with the slug out of the picture this is length.
 * - `notPermitted` — 403: the permission was lost between the sheet opening and save.
 */
export type Failure = 'invalid' | 'notPermitted' | 'other';

export function values(channel: Pick<ChannelRecord, 'displayName' | 'purpose' | 'header'>): Values {
  return { displayName: channel.displayName, purpose: channel.purpose ?? '', header: channel.header ?? '' };
}

/**
 * A direct or group message has a header and nothing else: its name is derived from who is in it,
 * and a purpose has no reader.
 */
export function editableFields(channelType: string): Set<Field> {
  switch (channelType) {
    case 'O':
    case 'P':
      return new Set<Field>(['displayName', 'purpose', 'header']);
    case 'D':
    case 'G':
      return new Set<Field>(['header']);
    default:
      return new Set();
  }
}

/**
 * Public and private channels gate on the matching `manage_*_channel_properties` permission; a
 * DM's header belongs to its participants and carries no permission.
 */
export function canEditProperties(channelType: string, permissions: ReadonlySet<string>): boolean {
  if (channelType === 'D' || channelType === 'G') return true;
  if (permissions.has('manage_system')) return channelType === 'O' || channelType === 'P';
  switch (channelType) {
    case 'O':
      return permissions.has('manage_public_channel_properties');
    case 'P':
      return permissions.has('manage_private_channel_properties');
    default:
      return false;
  }
}

/**
 * Public → private, never for the team's default channel: Town Square is the channel every member
 * is in by definition. The other direction is `canConvertToPublic`.
 */
export function canConvertToPrivate(channelType: string, channelName: string, permissions: ReadonlySet<string>): boolean {
  if (channelType !== 'O' || channelName === 'town-square') return false;
  return permissions.has('manage_system') || permissions.has('convert_public_channel_to_private');
}

/**
 * Private → public — what the desktop's channel settings let a system admin do (user, 2026-10-12):
 * `convert_private_channel_to_public` or `manage_system`. It exposes the history to the whole team,
 * so it is confirmed in words that say so.
 */
export function canConvertToPublic(channelType: string, permissions: ReadonlySet<string>): boolean {
  if (channelType !== 'P') return false;
  return permissions.has('manage_system') || permissions.has('convert_private_channel_to_public');
}

/** Foundation `trimmingCharacters(in: .whitespacesAndNewlines)`: White_Space at both ends. */
function trimmed(s: string): string {
  return s.replace(/^\p{White_Space}+|\p{White_Space}+$/gu, '');
}

/** Cuts `value` to `max` Characters (grapheme clusters) — the field's own limit, as typed. */
export function clamp(value: string, max: number): string {
  const chars = characters(value);
  return chars.length <= max ? value : chars.slice(0, max).join('');
}

/**
 * The difference between what was loaded and what is on screen, trimmed, restricted to the fields
 * this channel type actually has. Comparison is between TRIMMED values on both sides, so adding
 * and removing a trailing space cannot arm the save button.
 */
export function patch(original: Values, edited: Values, fields: ReadonlySet<Field>): Patch {
  const changed = (field: Field, before: string, after: string): string | undefined => {
    if (!fields.has(field)) return undefined;
    const t = trimmed(after);
    return t === trimmed(before) ? undefined : t;
  };
  const out: Patch = {};
  const d = changed('displayName', original.displayName, edited.displayName);
  const p = changed('purpose', original.purpose, edited.purpose);
  const h = changed('header', original.header, edited.header);
  if (d !== undefined) out.displayName = d;
  if (p !== undefined) out.purpose = p;
  if (h !== undefined) out.header = h;
  return out;
}

/**
 * Save is available when something changed AND every field on screen holds something the server
 * will take. A blank display name disables save even when the header is what changed: the request
 * carries the whole form's validity, not just the edited part.
 */
export function isSubmittable(original: Values, edited: Values, fields: ReadonlySet<Field>): boolean {
  if (fields.has('displayName')) {
    const length = characterCount(trimmed(edited.displayName));
    if (length < minDisplayName || length > maxDisplayName) return false;
  }
  return !isEmptyPatch(patch(original, edited, fields));
}

export function failure(statusCode: number | null | undefined): Failure {
  switch (statusCode) {
    case 400:
      return 'invalid';
    case 403:
      return 'notPermitted';
    default:
      return 'other';
  }
}

/** Namespace form, for `ChannelSettingsForm.patch(...)` call sites. */
export const ChannelSettingsForm = {
  minDisplayName,
  maxDisplayName,
  maxPurpose,
  maxHeader,
  values,
  editableFields,
  canEditProperties,
  canConvertToPrivate,
  canConvertToPublic,
  patch,
  isEmptyPatch,
  isSubmittable,
  failure,
  clamp,
} as const;
